import { useFetcher } from "@remix-run/react";
import { useEffect } from "react";
import type { Short } from "../../types";
import { ShortsRail } from "./ShortsRail";

/**
 * Self-fetching wrapper around {@link ShortsRail}.
 *
 * Mounted on the home page (and eventually category pages) without changing
 * the host loader's data contract. Uses Remix's `useFetcher().load(...)` to
 * call the `/shorts` route loader on the client after hydration — keeps
 * home SSR fast and lets the rail load progressively.
 *
 * If the rail's shorts list comes back empty, `ShortsRail` renders nothing,
 * so unmounted/empty state is invisible.
 */
type ShortsRailContainerProps = {
  /** Optional pre-fetched list — skips the client fetch when supplied. */
  shorts?: Short[];
  /** Optional cap on how many to display in the rail (full list still fetched). */
  limit?: number;
  title?: string;
  viewMoreHref?: string;
};

export function ShortsRailContainer({
  shorts: prefetched,
  limit,
  title,
  viewMoreHref,
}: ShortsRailContainerProps) {
  const fetcher = useFetcher<{ shorts: Short[] }>();

  useEffect(() => {
    if (prefetched) return; // host pre-fetched, no client load needed
    if (fetcher.state === "idle" && fetcher.data == null) {
      fetcher.load("/shorts");
    }
  }, [fetcher, prefetched]);

  const list = prefetched ?? fetcher.data?.shorts ?? [];
  const visible = typeof limit === "number" ? list.slice(0, limit) : list;

  return <ShortsRail shorts={visible} title={title} viewMoreHref={viewMoreHref} />;
}
